import { ApolloProvider } from '@apollo/react-common';
import {
  Flex,
  Loader,
  mergeThemes,
  Provider as StardustThemeProvider,
  themes
} from '@fluentui/react-northstar';
import { setRTL } from '@fluentui/react/lib/Utilities';
import { ThemeNames } from '@smb/fabric-teams-theme';
import i18n from 'i18next';
import * as React from 'react';
import { I18nextProvider } from 'react-i18next';
import { HashRouter, Route, Switch } from 'react-router-dom';
import { apolloClient, clientPromise, getTokenPromise } from '../apolloClient';
import { FullPageLoadingContextProvider } from '../components/full-page-loading/FullPageLoadingContextProvider';
import { AnnouncerContextProvider } from '../shared/accessibility/announcer/AnnouncerContext';
import { ConnectedBannerErrorContextProvider } from '../shared/banner-error/ConnectedBannerErrorContext';
import { initializeEcsClient } from '../shared/ecs/ecsClient';
import { EcsConfigProvider } from '../shared/ecs/EcsConfigProvider';
import { TeamsThemeForFabricUI } from '../shared/fabric-themes/teams/TeamsThemeForFabricUI';
import { isRtlLangCode } from '../shared/localization/is-rtl';
import { setLocaleFromTeamsContext } from '../shared/localization/localize';
import { NavigationContextProvider } from '../shared/navigation/navigation-context/NavigationContextProvider';
import { IContext } from '../shared/teams-sdk/teamsSdk';
import { getTeamsContext } from '../shared/teams-tabs/get-teams-context';
import { initializeTeamsSdk } from '../shared/teams-tabs/initialize-teams-sdk';
import { TeamsContextProvider } from '../shared/teams-tabs/teams-context/TeamsContext';
import { onThemeChangeListener } from '../shared/teams-theme/on-theme-change-listener';
import { Logger } from '../shared/telemetry/Logger';
import { ConnectedSessionContextProvider } from '../shared/telemetry/session-context/ConnectedSessionContext';
import { ConnectedTelemetryIngestionContextProvider } from '../shared/telemetry/telemetry-ingestion-context/ConnectedTelemetryIngestionContextProvider';
import { flushTelemetry } from '../shared/telemetry/telemetryClient';
import { TicketsNextBackStateContextProvider } from '../shared/tickets-next-back-state/TicketsNextBackStateContext';
import { PageLoadContextProvider } from '../shared/utils/page-load-helper/page-load-context';
import { getAppStyles, IAppStyles } from './App.styles';
import { IAppProps, IAppState } from './App.types';
import { Paths } from './routes';

export const ConnectedEmailRoot = React.lazy(() =>
  import('../email/email-root/ConnectedEmailRoot')
);

const ConnectedChannelTabConfigure = React.lazy(() =>
  import('../components/channel-tab-configure/ConnectedChannelTabConfigure')
);

const ConnectedChannelTabRemove = React.lazy(() =>
  import('../components/channel-tab-remove/ConnectedChannelTabRemove')
);

export class App extends React.Component<IAppProps, IAppState> {
  constructor(props: IAppProps) {
    super(props);

    this.state = {
      theme: themes.teams,
      isLoaded: false,
      loadError: null,
      isRtl: false
    };
  }

  public render(): JSX.Element {
    const styles = getAppStyles(this.state.theme);

    return (
      <AnnouncerContextProvider>
        <StardustThemeProvider
          theme={mergeThemes(this.state.theme, {
            staticStyles: [
              {
                a: {
                  textDecoration: 'none'
                }
              }
            ]
          })}
          styles={styles.provider}
          rtl={this.state.isRtl}
        >
          <TeamsThemeForFabricUI>
            <I18nextProvider i18n={i18n}>
              <React.Suspense fallback={this._renderLoader(styles)}>
                {this.state.loadError !== null
                  ? this._renderError(styles)
                  : this.state.isLoaded
                  ? this._renderMainApp()
                  : this._renderLoader(styles)}
              </React.Suspense>
            </I18nextProvider>
          </TeamsThemeForFabricUI>
        </StardustThemeProvider>
      </AnnouncerContextProvider>
    );
  }

  public async componentDidMount(): Promise<void> {
    await initializeTeamsSdk();
    getTeamsContext(this._onGetTeamsContext);
    window.addEventListener('beforeunload', this._componentCleanUp);
    onThemeChangeListener(this._onTeamsThemeChange);
  }

  public componentWillUnmount(): void {
    flushTelemetry();
  }

  public componentDidCatch(error: Error, info: React.ErrorInfo): void {
    Logger.logException(
      error,
      `Error: ${error ? error.name : 'no op'} with message: ${
        error ? error.message : 'no message'
      }`
    );
    console.log(
      'Error with Stack Trace: ',
      error ? error.message : 'no op',
      info.componentStack
    );
    flushTelemetry();
  }

  private _componentCleanUp(): void {
    window.removeEventListener('beforeunload', this._componentCleanUp);
    flushTelemetry();
  }

  private _renderLoader = (styles: IAppStyles): JSX.Element => {
    return (
      <Flex hAlign="center" vAlign="center" column styles={styles.rootFlex}>
        <Loader />
      </Flex>
    );
  };

  private _renderError = (styles: IAppStyles): JSX.Element => {
    return (
      <Flex hAlign="center" vAlign="center" column styles={styles.rootFlex}>
        {this.state.loadError && (
          <div>
            <h2>
              {this.state.loadError.name + '::' + this.state.loadError.message}
            </h2>
            {process.env.NODE_ENV === 'development' && (
              <div>
                <h4>
                  (you may need to open Teams in the sso test container:
                  <a href="https://aka.ms/teams-sso-test" target="_top">
                    https://aka.ms/teams-sso-test
                  </a>
                  )
                </h4>
                <div>{this.state.loadError.stack}</div>
              </div>
            )}
          </div>
        )}
      </Flex>
    );
  };

  private _renderMainApp = (): JSX.Element => {
    return (
      <>
        {/** Safe to "!" apolloClient here because it will have been resolved and set prior to render main app */}
        <ApolloProvider client={apolloClient!}>
          <TeamsContextProvider>
            <ConnectedTelemetryIngestionContextProvider>
              <HashRouter>
                <ConnectedSessionContextProvider>
                  <EcsConfigProvider>
                    <FullPageLoadingContextProvider>
                      <ConnectedBannerErrorContextProvider>
                        <PageLoadContextProvider>
                          <TicketsNextBackStateContextProvider>
                            <NavigationContextProvider>
                              <Switch>
                                <Route
                                  path={Paths.ChannelTabConfig}
                                  component={ConnectedChannelTabConfigure}
                                />
                                <Route
                                  path={Paths.EmailRoot}
                                  component={ConnectedEmailRoot}
                                />
                                <Route
                                  path={Paths.ChannelTabRemove}
                                  component={ConnectedChannelTabRemove}
                                />
                                {/** TODO: Replace a 404 page or "Oops" page, when we have that design */}
                                <Route
                                  component={ConnectedChannelTabConfigure}
                                />
                              </Switch>
                            </NavigationContextProvider>
                          </TicketsNextBackStateContextProvider>
                        </PageLoadContextProvider>
                      </ConnectedBannerErrorContextProvider>
                    </FullPageLoadingContextProvider>
                  </EcsConfigProvider>
                </ConnectedSessionContextProvider>
              </HashRouter>
            </ConnectedTelemetryIngestionContextProvider>
          </TeamsContextProvider>
        </ApolloProvider>
      </>
    );
  };

  private _onGetTeamsContext = async (context: IContext) => {
    const isRtl = isRtlLangCode(context.locale);
    setRTL(isRtl);
    clientPromise(context.channelId)
      .then(() => getTokenPromise())
      .then(() => {
        this.setState({
          isLoaded: true,
          isRtl
        });
      })
      .catch(err => {
        this.setState({
          isLoaded: true,
          loadError: err
        });
      });

    await this._onTeamsThemeChange(context.theme);
    await setLocaleFromTeamsContext(context);
    await initializeEcsClient(context);
    Logger.initialize(context);
  };

  private _onTeamsThemeChange = (themeName?: string): Promise<void> => {
    return new Promise(resolve => {
      if (themeName === ThemeNames.dark) {
        this.setState(
          {
            theme: themes.teamsDark
          },
          resolve
        );
      } else if (themeName === ThemeNames.contrast) {
        this.setState(
          {
            theme: themes.teamsHighContrast
          },
          resolve
        );
      } else {
        this.setState(
          {
            theme: themes.teams
          },
          resolve
        );
      }
    });
  };
}
